import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CustomerSelect } from "@/components/molecules/customer-select";
import { FormSelect } from "@/components/molecules/form-select";
import { useSubmitQualification } from "@/features/qualifications/hooks/use-qualifications";
import { useForm } from "@/features/qualifications/hooks/use-qualification-forms";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { useTranslation } from "@/features/utils/i18n";
import { QuestionFields } from "./question-field";

interface CreateQualificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateQualificationDialog = ({
  open,
  onOpenChange,
}: CreateQualificationDialogProps) => {
  const { t } = useTranslation("qualifications");
  const submitQualification = useSubmitQualification();
  const [customerId, setCustomerId] = useState("");
  const [formId, setFormId] = useState("");
  const [responses, setResponses] = useState<Record<string, unknown>>({});

  const { data: form, isLoading: isLoadingForm } = useForm(formId);
  const questions = form?.questions?.questions || [];

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setCustomerId("");
      setFormId("");
      setResponses({});
    }
  }, [open]);

  // Reset responses when form changes
  useEffect(() => {
    setResponses({});
  }, [formId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!customerId) {
      toast.error(t("submissions.create.select_customer"));
      return;
    }

    if (!formId) {
      toast.error(t("submissions.create.select_form"));
      return;
    }

    // Validate required questions
    for (const question of questions) {
      const value = responses[question.id];
      if (question.required) {
        // Check for undefined/null (not answered)
        if (value === undefined || value === null) {
          toast.error(
            t("submissions.create.answer_required", {
              question: question.label,
            }),
          );
          return;
        }
        // For text/select, also check for empty string
        if (
          (question.type === "text" || question.type === "select") &&
          value === ""
        ) {
          toast.error(
            t("submissions.create.answer_required", {
              question: question.label,
            }),
          );
          return;
        }
      }
    }

    try {
      await submitQualification.mutateAsync({
        form_id: formId,
        customer_id: customerId,
        responses,
      });
      toast.success(t("submissions.toasts.created"));
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("submissions.toasts.create_failed"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("submissions.create.title")}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Customer Selection */}
            <div className="space-y-2">
              <Label>
                {t("submissions.create.customer")}{" "}
                <span className="text-red-500">*</span>
              </Label>
              <CustomerSelect value={customerId} onChange={setCustomerId} />
            </div>

            {/* Form Selection */}
            <div className="space-y-2">
              <Label>
                {t("submissions.create.form")}{" "}
                <span className="text-red-500">*</span>
              </Label>
              <FormSelect value={formId} onChange={setFormId} status="active" />
            </div>

            {/* Questions */}
            {isLoadingForm && formId && (
              <div className="flex items-center justify-center py-8">
                <Spinner className="h-6 w-6" />
              </div>
            )}

            {!isLoadingForm && questions.length > 0 && (
              <>
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-4">
                    {t("submissions.create.questions")}
                  </h3>
                </div>
                <QuestionFields
                  questions={questions}
                  responses={responses}
                  onChange={setResponses}
                />
              </>
            )}

            {!isLoadingForm && formId && questions.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {t("submissions.create.no_questions")}
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("submissions.common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                submitQualification.isPending ||
                !customerId ||
                !formId ||
                questions.length === 0
              }
            >
              {submitQualification.isPending
                ? t("submissions.create.submitting")
                : t("submissions.create.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
